/**
 * CoverageChangeCard - Rich card display for a single coverage's changes.
 *
 * Coverage-specific layout using shared change utilities:
 * - Header with carrier name, policy type, category, status badges
 * - Key fields grid, named insured, carrier contacts
 * - Collapsible sections for benefits, claims, adjusters, vehicles, policy details
 * - Amber change indicators with old-value tooltips
 * - Transfer checkboxes + "Mark as Transferred" button
 * - UPDATED/SAME: unchanged content behind ShowMoreSection
 */

import {
  Mail,
  MapPin,
  Phone,
  Printer,
  Shield,
} from 'lucide-react';
import * as React from 'react';

import type { BadgeColor } from '@/components/ui/catalyst/badge';
import { Badge } from '@/components/ui/catalyst/badge';
import { cn } from '@/lib/utils';
import type {
  BenefitType,
  ChangedCoverageEntity,
  CoverageCategory,
  CoverageStatus,
  IdentifierType,
  PolicyType,
} from '@/services/api/generated/crossMatterChangesService';

import { buildCheckableItems, type CheckableFieldConfig } from '../utils/buildCheckableItems';
import { formatEnumValue } from '../utils/formatting';
import { useEntityChanges } from '../utils/useEntityChanges';
import { useTransferAction } from '../utils/useTransferAction';
import { useTransferChecklist } from '../utils/useTransferChecklist';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { NestedChangeIndicator } from './ChangeIndicators';
import { CollapsibleSection } from './CollapsibleSection';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { EntityField } from './EntityField';
import { EventsFallbackWithChecklist } from './EventsFallbackWithChecklist';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';
import { TransferButton } from './TransferButton';
import { TransferCheckbox } from './TransferCheckbox';

// ============================================================================
// Coverage-specific config
// ============================================================================

const COVERAGE_STATUS_COLORS: Record<CoverageStatus, BadgeColor> = {
  NEEDS_APPROVAL: 'amber',
  APPROVED: 'green',
  REJECTED: 'red',
  NOT_APPLICABLE: 'zinc',
};


const CATEGORY_LABELS: Record<CoverageCategory, string> = {
  FIRST_PARTY: '1P',
  FIRST_PARTY_HEALTH: 'Health',
  THIRD_PARTY: '3P',
  NOT_LIABLE_INVOLVED_PARTY: 'Not-Liable',
  PROPERTY: 'Premises',
  UNCLEAR: 'Unclear',
  WORKERS_COMPENSATION: 'WC',
};

const POLICY_TYPE_LABELS: Record<PolicyType, string> = {
  AUTO: 'Auto',
  HOMEOWNERS: 'Home',
  COMMERCIAL: 'Commercial',
  HEALTH: 'Health',
  UMBRELLA: 'Umbrella',
  WORKERS_COMPENSATION: 'WC',
  GENERAL_LIABILITY: 'GL',
  OTHER: 'Policy',
};

const BENEFIT_TYPE_LABELS: Record<BenefitType, string> = {
  PERSONAL_INJURY_PROTECTION: 'PIP',
  MEDICAL_PAYMENTS: 'MedPay',
  UNINSURED_MOTORIST: 'UM',
  UNDER_INSURED_MOTORIST: 'UIM',
  BODILY_INJURY_LIABILITY: 'BI',
  PROPERTY_DAMAGE_LIABILITY: 'PD',
  COMPREHENSIVE_AUTO: 'Comprehensive',
  COLLISION_AUTO: 'Collision',
  WORKERS_COMPENSATION: "Workers' Comp",
  OTHER: 'Other',
};

const IDENTIFIER_TYPE_LABELS: Record<IdentifierType, string> = {
  POLICY_NUMBER: 'Policy #',
  MEMBER_ID: 'Member ID',
  GROUP_NUMBER: 'Group #',
  OTHER: 'ID',
};

const KEY_FIELDS: FieldConfig[] = [
  { key: 'liability_status', label: 'Liability', isEnum: true },
  { key: 'enrichment_source', label: 'Source', isEnum: true },
];

const SECONDARY_FIELDS: { key: string; label: string }[] = [
  { key: 'carrier_v2_id', label: 'Carrier Directory ID' },
  { key: 'carrier_division_id', label: 'Division ID' },
  { key: 'property_id', label: 'Property ID' },
];

function getHandleIcon(type: string) {
  switch (type) {
    case 'PHONE':
      return Phone;
    case 'EMAIL':
      return Mail;
    case 'FAX':
      return Printer;
    case 'MAILING_ADDRESS':
      return MapPin;
    default:
      return Phone;
  }
}

function formatDollar(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const COVERAGE_CHECKABLE_FIELDS: CheckableFieldConfig[] = [
  { key: 'status', type: 'field' },
  { key: 'category', type: 'field' },
  { key: 'policy_type', type: 'field' },
  { key: 'liability_status', type: 'field' },
  { key: 'enrichment_source', type: 'field' },
  { key: 'carrier', type: 'nested' },
  { key: 'named_insured', type: 'nested' },
  { key: 'policy_identifiers', type: 'array' },
  { key: 'benefits', type: 'array' },
  { key: 'claims', type: 'array' },
  { key: 'adjusters', type: 'array' },
  { key: 'vehicles', type: 'array' },
  { key: 'policy_details', type: 'array' },
  { key: 'subrogator', type: 'nested' },
  { key: 'enrichment_metadata', type: 'nested' },
  { key: 'carrier_v2_id', type: 'field' },
  { key: 'carrier_division_id', type: 'field' },
  { key: 'property_id', type: 'field' },
];

// ============================================================================
// Main Component
// ============================================================================

export const CoverageChangeCard: React.FC<{
  entity: ChangedCoverageEntity;
}> = ({ entity }) => {
  const displayData =
    entity.change_type === 'DELETED'
      ? (entity.old ?? entity.current)
      : (entity.current ?? entity.old);

  const changes = useEntityChanges(
    entity.change_type,
    entity.old,
    entity.current,
  );

  // Compute checkable items for transfer checklist

  const checkableItems = React.useMemo<string[]>(
    () =>
      buildCheckableItems(
        entity.change_type,
        displayData as Record<string, unknown> | undefined,
        entity.events,
        changes,
        COVERAGE_CHECKABLE_FIELDS,
      ),
    [entity.change_type, entity.events, changes, displayData],
  );

  const checklist = useTransferChecklist(entity.change_type, checkableItems);
  const transferEventIds = React.useMemo(() => entity.events.map((e) => e.id), [entity.events]);
  const transfer = useTransferAction(transferEventIds);

  if (!displayData) return null;

  const data = displayData as unknown as Record<string, unknown>;

  // Header fields
  const policyType = displayData.policy_type;
  const {category} = displayData;
  const {status} = displayData;
  const statusChanged = changes.isFieldChanged('status');
  const categoryChanged = changes.isFieldChanged('category');
  const policyTypeChanged = changes.isFieldChanged('policy_type');

  // Carrier
  const carrier = displayData.carrier as Record<string, unknown> | undefined;
  const carrierChanged =
    changes.hasChangesUnder('carrier') || changes.isFieldChanged('carrier');
  const carrierHandles = (carrier?.handles ?? []) as Array<Record<string, unknown>>;

  // Named insured
  const namedInsured = (displayData.named_insured ?? []) as string[];
  const namedInsuredChanged =
    changes.isFieldChanged('named_insured') ||
    changes.hasChangesUnder('named_insured');

  // Policy identifiers
  const policyIdentifiers = (displayData.policy_identifiers ?? []) as Array<
    Record<string, unknown>
  >;
  const policyIdentifiersChanged = changes.hasChangesUnder('policy_identifiers');

  // Arrays
  const benefits = (displayData.benefits ?? []) as Array<Record<string, unknown>>;
  const benefitsChanged = changes.hasChangesUnder('benefits');

  const claims = (displayData.claims ?? []) as Array<Record<string, unknown>>;
  const claimsChanged = changes.hasChangesUnder('claims');

  const adjusters = (displayData.adjusters ?? []) as Array<Record<string, unknown>>;
  const adjustersChanged = changes.hasChangesUnder('adjusters');

  const vehicles = (displayData.vehicles ?? []) as Array<Record<string, unknown>>;
  const vehiclesChanged = changes.hasChangesUnder('vehicles');

  const policyDetails = (displayData.policy_details ?? []) as Array<
    Record<string, unknown>
  >;
  const policyDetailsChanged = changes.hasChangesUnder('policy_details');

  // Subrogator
  const subrogator = displayData.subrogator as Record<string, unknown> | undefined;
  const subrogatorChanged =
    changes.hasChangesUnder('subrogator') ||
    changes.isFieldChanged('subrogator');

  // Secondary
  const visibleSecondaryFields = SECONDARY_FIELDS.filter(
    (f) => data[f.key] || changes.isFieldChanged(f.key),
  );

  // Enrichment metadata
  const enrichmentMetadataChanged =
    changes.hasChangesUnder('enrichment_metadata') ||
    changes.isFieldChanged('enrichment_metadata');

  // Events fallback
  const isUpdatedWithoutDiffs =
    entity.change_type === 'UPDATED' && changes.totalChanges === 0;

  const hasExpandedContent =
    KEY_FIELDS.some((f) => {
      const val = data[f.key];
      const hasValue = val !== null && val !== undefined && val !== '';
      return hasValue || changes.isFieldChanged(f.key);
    }) ||
    statusChanged ||
    categoryChanged ||
    policyTypeChanged ||
    namedInsured.length > 0 ||
    namedInsuredChanged ||
    policyIdentifiers.length > 0 ||
    carrierHandles.length > 0 ||
    carrierChanged ||
    benefits.length > 0 ||
    claims.length > 0 ||
    adjusters.length > 0 ||
    vehicles.length > 0 ||
    policyDetails.length > 0 ||
    (subrogator != null) ||
    visibleSecondaryFields.length > 0 ||
    enrichmentMetadataChanged ||
    isUpdatedWithoutDiffs;

  // Check if there's unchanged content for ShowMoreSection
  const hasUnchangedContent =
    (entity.change_type === 'UPDATED' || entity.change_type === 'SAME') &&
    (KEY_FIELDS.some((f) => {
      const val = data[f.key];
      const hasValue = val !== null && val !== undefined && val !== '';
      return hasValue && !changes.isFieldChanged(f.key);
    }) ||
      (!namedInsuredChanged && namedInsured.length > 0) ||
      (!carrierChanged && (carrierHandles.length > 0 || !!carrier?.address)) ||
      (!policyIdentifiersChanged && policyIdentifiers.length > 0) ||
      (!benefitsChanged && benefits.length > 0) ||
      (!claimsChanged && claims.length > 0) ||
      (!adjustersChanged && adjusters.length > 0) ||
      (!vehiclesChanged && vehicles.length > 0) ||
      (!policyDetailsChanged && policyDetails.length > 0) ||
      (!subrogatorChanged && subrogator) ||
      visibleSecondaryFields.some((f) => !changes.isFieldChanged(f.key)));

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={entity.name}
      description={entity.description}
      icon={Shield}
      iconBgClassName="bg-emerald-50"
      iconTextClassName="text-emerald-600"
      badges={
        <>
          {policyType && (
            <Badge color="zinc" className="text-[10px] shrink-0">
              {POLICY_TYPE_LABELS[policyType] ?? formatEnumValue(policyType)}
            </Badge>
          )}
          {category && (
            <Badge color="zinc" className="text-[10px] shrink-0">
              {CATEGORY_LABELS[category] ?? formatEnumValue(category)}
            </Badge>
          )}
        </>
      }
      changeCount={changes.totalChanges}
      eventCount={entity.events.length}
      checklist={checklist}
      hasExpandedContent={hasExpandedContent}
    >
      {/* Events fallback */}
      {isUpdatedWithoutDiffs && (
        <EventsFallbackWithChecklist events={entity.events} checklist={checklist} />
      )}

      {/* Changed header fields (status, category, policy_type) shown prominently */}
      {statusChanged && status && (
        <ChangedFieldHighlight
          label="Status"
          fieldKey="status"
          changes={changes}
          checklist={checklist}
        >
          <Badge
            color={COVERAGE_STATUS_COLORS[status] ?? 'zinc'}
            className="text-xs"
          >
            {formatEnumValue(status)}
          </Badge>
        </ChangedFieldHighlight>
      )}

      {categoryChanged && category && (
        <ChangedFieldHighlight
          label="Category"
          fieldKey="category"
          changes={changes}
          checklist={checklist}
        >
          <span className="text-sm text-gray-900">
            {CATEGORY_LABELS[category] ?? formatEnumValue(category)}
          </span>
        </ChangedFieldHighlight>
      )}

      {policyTypeChanged && policyType && (
        <ChangedFieldHighlight
          label="Policy Type"
          fieldKey="policy_type"
          changes={changes}
          checklist={checklist}
        >
          <span className="text-sm text-gray-900">
            {POLICY_TYPE_LABELS[policyType] ?? formatEnumValue(policyType)}
          </span>
        </ChangedFieldHighlight>
      )}

      {/* Key fields grid - only changed fields for UPDATED/SAME */}
      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        checklist={checklist}
        changeType={entity.change_type}
      />

      {/* Named Insured - changed version */}
      {namedInsuredChanged && (
        <ChangedFieldHighlight
          label="Named Insured"
          fieldKey="named_insured"
          changes={changes}
          checklist={checklist}
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            {namedInsured.length > 0 ? (
              namedInsured.map((name) => (
                <Badge key={name} color="zinc" className="text-[10px]">
                  {name}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-gray-400">{'\u2014'}</span>
            )}
          </div>
        </ChangedFieldHighlight>
      )}

      {/* Carrier contact section - changed version */}
      {carrierChanged && (
        <ChangedFieldHighlight
          label="Carrier"
          fieldKey="carrier"
          changes={changes}
          checklist={checklist}
        >
          <div className="space-y-0.5">
            {carrierHandles.map((handle, idx) => {
              const Icon = getHandleIcon(handle.type as string);
              const value =
                (handle.value as string) ||
                (handle.address_line1 as string) ||
                '\u2014';
              return (
                <div key={idx} className="flex items-center gap-1.5 px-1 py-0.5">
                  <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-700 truncate">{value}</span>
                </div>
              );
            })}
            {!!carrier?.address && (
              <div className="flex items-center gap-1.5 px-1 py-0.5">
                <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-700 truncate">
                  {String(carrier.address)}
                </span>
              </div>
            )}
          </div>
        </ChangedFieldHighlight>
      )}

      {/* Policy Identifiers - changed version */}
      {policyIdentifiersChanged && policyIdentifiers.length > 0 && (
        <CollapsibleSection
          title="Policy Identifiers"
          count={policyIdentifiers.length}
          hasChanges={policyIdentifiersChanged}
          changeType={entity.change_type}
        >
          {policyIdentifiers.map((id, idx) => {
            const idType = id.identifier_type as string;
            const idValue = id.identifier_value as string;
            const itemChanged = changes.hasChangesUnder(
              `policy_identifiers.${idx}`,
            );
            return (
              <div
                key={idx}
                className={cn(
                  'flex items-center gap-2',
                  checklist.showCheckboxes && itemChanged && 'gap-2',
                )}
              >
                <div
                  className={cn(
                    'flex-1 flex items-baseline gap-1.5 px-1 py-0.5',
                    itemChanged && 'rounded-md px-3 py-1.5 bg-amber-50/60',
                  )}
                >
                  <span className="text-xs text-gray-400 shrink-0">
                    {IDENTIFIER_TYPE_LABELS[idType as IdentifierType] ??
                      idType}
                    :
                  </span>
                  <span className="text-sm text-gray-700">{idValue}</span>
                  {itemChanged && (
                    <NestedChangeIndicator
                      prefix={`policy_identifiers.${idx}`}
                      changes={changes}
                    />
                  )}
                </div>
                {checklist.showCheckboxes && itemChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(
                      `policy_identifiers.${idx}`,
                    )}
                    onToggle={() =>
                      checklist.toggle(`policy_identifiers.${idx}`)
                    }
                  />
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {/* Benefits - changed version */}
      {benefitsChanged && benefits.length > 0 && (
        <CollapsibleSection
          title="Benefits"
          count={benefits.length}
          hasChanges={benefitsChanged}
          changeType={entity.change_type}
        >
          <div className="flex flex-wrap items-start gap-1.5">
            {benefits.map((benefit, idx) => {
              const type = benefit.benefit_type as BenefitType | undefined;
              const limitations = benefit.limitations as Record<
                string,
                number
              > | null;
              const isStacked = benefit.is_stacked as boolean | null;
              const itemChanged = changes.hasChangesUnder(`benefits.${idx}`);
              const hasLimitations =
                limitations && Object.keys(limitations).length > 0;
              const limitationsText = hasLimitations
                ? Object.entries(limitations)
                    .map(
                      ([label, amount]) =>
                        `${formatDollar(amount)}/${label}`,
                    )
                    .join(', ')
                : null;

              return (
                <div
                  key={idx}
                  className={cn(
                    'flex items-center gap-1.5',
                    itemChanged && 'rounded-md px-2 py-1 bg-amber-50/60',
                  )}
                >
                  {type && (
                    <Badge color="zinc" className="text-[10px]">
                      {BENEFIT_TYPE_LABELS[type] ?? formatEnumValue(type)}
                    </Badge>
                  )}
                  {limitationsText && (
                    <span className="text-xs text-gray-600">
                      {limitationsText}
                    </span>
                  )}
                  {isStacked != null && (
                    <span className="text-[10px] text-gray-400">
                      {isStacked ? 'Stacked' : 'Not stacked'}
                    </span>
                  )}
                  {itemChanged && (
                    <NestedChangeIndicator
                      prefix={`benefits.${idx}`}
                      changes={changes}
                    />
                  )}
                  {checklist.showCheckboxes && itemChanged && (
                    <TransferCheckbox
                      checked={checklist.isChecked(`benefits.${idx}`)}
                      onToggle={() => checklist.toggle(`benefits.${idx}`)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* Claims - changed version */}
      {claimsChanged && claims.length > 0 && (
        <CollapsibleSection
          title="Claims"
          count={claims.length}
          hasChanges={claimsChanged}
          changeType={entity.change_type}
        >
          {claims.map((claim, idx) => {
            const claimId = claim.claim_identifier as string | undefined;
            const type = claim.benefit_type as BenefitType | undefined;
            const itemChanged = changes.hasChangesUnder(`claims.${idx}`);
            return (
              <div key={idx} className="flex items-center gap-2">
                <div
                  className={cn(
                    'flex-1 flex items-center gap-1.5 px-1 py-0.5',
                    itemChanged && 'rounded-md px-3 py-1.5 bg-amber-50/60',
                  )}
                >
                  <span className="text-sm text-gray-700">
                    {claimId || '\u2014'}
                  </span>
                  {type && (
                    <Badge color="zinc" className="text-[10px]">
                      {BENEFIT_TYPE_LABELS[type] ?? formatEnumValue(type)}
                    </Badge>
                  )}
                  {itemChanged && (
                    <NestedChangeIndicator
                      prefix={`claims.${idx}`}
                      changes={changes}
                    />
                  )}
                </div>
                {checklist.showCheckboxes && itemChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(`claims.${idx}`)}
                    onToggle={() => checklist.toggle(`claims.${idx}`)}
                  />
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {/* Adjusters - changed version */}
      {adjustersChanged && adjusters.length > 0 && (
        <CollapsibleSection
          title="Adjusters"
          count={adjusters.length}
          hasChanges={adjustersChanged}
          changeType={entity.change_type}
        >
          {adjusters.map((adj, idx) => {
            const adjName = adj.name as string | undefined;
            const type = adj.benefit_type as BenefitType | undefined;
            const handles = (adj.handles ?? []) as Array<
              Record<string, unknown>
            >;
            const itemChanged = changes.hasChangesUnder(
              `adjusters.${idx}`,
            );
            return (
              <div key={idx} className="flex items-start gap-2">
                <div
                  className={cn(
                    'flex-1 px-1 py-0.5',
                    itemChanged && 'rounded-md px-3 py-1.5 bg-amber-50/60',
                  )}
                >
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm text-gray-700">
                      {adjName || 'Unnamed Adjuster'}
                    </span>
                    {type && (
                      <Badge color="zinc" className="text-[10px]">
                        {BENEFIT_TYPE_LABELS[type] ??
                          formatEnumValue(type)}
                      </Badge>
                    )}
                    {itemChanged && (
                      <NestedChangeIndicator
                        prefix={`adjusters.${idx}`}
                        changes={changes}
                      />
                    )}
                  </div>
                  {handles.length > 0 && (
                    <div className="mt-0.5 space-y-0.5">
                      {handles.map((h, hIdx) => {
                        const Icon = getHandleIcon(h.type as string);
                        return (
                          <div
                            key={hIdx}
                            className="flex items-center gap-1.5"
                          >
                            <Icon className="h-3 w-3 text-gray-400 shrink-0" />
                            <span className="text-xs text-gray-600 truncate">
                              {(h.value as string) || '\u2014'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {checklist.showCheckboxes && itemChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(`adjusters.${idx}`)}
                    onToggle={() => checklist.toggle(`adjusters.${idx}`)}
                  />
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {/* Vehicles - changed version */}
      {vehiclesChanged && vehicles.length > 0 && (
        <CollapsibleSection
          title="Vehicles"
          count={vehicles.length}
          hasChanges={vehiclesChanged}
          changeType={entity.change_type}
        >
          {vehicles.map((v, idx) => {
            const year = v.year as number | undefined;
            const make = v.make as string | undefined;
            const model = v.model as string | undefined;
            const vin = v.vin as string | undefined;
            const plate = v.license_plate as string | undefined;
            const color = v.color as string | undefined;
            const itemChanged = changes.hasChangesUnder(`vehicles.${idx}`);
            const title = [year, make, model]
              .filter(Boolean)
              .join(' ');
            return (
              <div key={idx} className="flex items-start gap-2">
                <div
                  className={cn(
                    'flex-1 px-1 py-0.5',
                    itemChanged && 'rounded-md px-3 py-1.5 bg-amber-50/60',
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-gray-700">
                      {title || 'Vehicle'}
                    </span>
                    {color && (
                      <span className="text-xs text-gray-400">
                        {color}
                      </span>
                    )}
                    {itemChanged && (
                      <NestedChangeIndicator
                        prefix={`vehicles.${idx}`}
                        changes={changes}
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    {vin && <span>VIN: {vin}</span>}
                    {plate && <span>Plate: {plate}</span>}
                  </div>
                </div>
                {checklist.showCheckboxes && itemChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(`vehicles.${idx}`)}
                    onToggle={() => checklist.toggle(`vehicles.${idx}`)}
                  />
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {/* Policy Details - changed version */}
      {policyDetailsChanged && policyDetails.length > 0 && (
        <CollapsibleSection
          title="Policy Details"
          count={policyDetails.length}
          hasChanges={policyDetailsChanged}
          changeType={entity.change_type}
        >
          {policyDetails.map((detail, idx) => {
            const key = detail.key as string;
            const value = detail.value as string;
            const itemChanged = changes.hasChangesUnder(
              `policy_details.${idx}`,
            );
            return (
              <div key={idx} className="flex items-center gap-2">
                <div
                  className={cn(
                    'flex-1 flex items-baseline gap-1.5 px-1 py-0.5',
                    itemChanged && 'rounded-md px-3 py-1.5 bg-amber-50/60',
                  )}
                >
                  <span className="text-xs text-gray-400 shrink-0">
                    {key}:
                  </span>
                  <span className="text-sm text-gray-700 truncate">
                    {value}
                  </span>
                  {itemChanged && (
                    <NestedChangeIndicator
                      prefix={`policy_details.${idx}`}
                      changes={changes}
                    />
                  )}
                </div>
                {checklist.showCheckboxes && itemChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(`policy_details.${idx}`)}
                    onToggle={() =>
                      checklist.toggle(`policy_details.${idx}`)
                    }
                  />
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {/* Subrogator - changed version */}
      {subrogatorChanged && subrogator && (
        <ChangedFieldHighlight
          label="Subrogator"
          fieldKey="subrogator"
          changes={changes}
          checklist={checklist}
        >
          <span className="text-sm text-gray-900">
            {(subrogator.name as string) || 'Unnamed'}
          </span>
        </ChangedFieldHighlight>
      )}

      {/* Enrichment metadata changes */}
      {enrichmentMetadataChanged && (
        <div className="flex items-center gap-2">
          <p className="flex items-center gap-1.5 text-xs text-gray-900 px-1 flex-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
            Enrichment metadata updated
          </p>
          {checklist.showCheckboxes && (
            <TransferCheckbox
              checked={checklist.isChecked('enrichment_metadata')}
              onToggle={() => checklist.toggle('enrichment_metadata')}
            />
          )}
        </div>
      )}

      {/* References - changed version */}
      {visibleSecondaryFields.some((f) => changes.isFieldChanged(f.key)) && (
        <CollapsibleSection
          title="References"
          count={visibleSecondaryFields.length}
          hasChanges={visibleSecondaryFields.some((f) =>
            changes.isFieldChanged(f.key),
          )}
          changeType={entity.change_type}
        >
          {visibleSecondaryFields
            .filter((f) => changes.isFieldChanged(f.key))
            .map((f) => (
              <div key={f.key} className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <EntityField
                    label={f.label}
                    value={data[f.key]}
                    fieldKey={f.key}
                    changes={changes}
                  />
                </div>
                {checklist.showCheckboxes && (
                  <TransferCheckbox
                    checked={checklist.isChecked(f.key)}
                    onToggle={() => checklist.toggle(f.key)}
                    className="mt-2.5"
                  />
                )}
              </div>
            ))}
        </CollapsibleSection>
      )}

      {/* ShowMoreSection for unchanged content on UPDATED/SAME entities */}
      {hasUnchangedContent && (
        <ShowMoreSection changeType={entity.change_type}>
          {/* Unchanged key fields */}
          <KeyFieldsGridUnchanged
            fields={KEY_FIELDS}
            data={data}
            changes={changes}
            checklist={checklist}
          />

          {/* Named Insured - unchanged version */}
          {!namedInsuredChanged && namedInsured.length > 0 && (
            <div className="flex-1">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-xs font-medium text-gray-500">
                  Named Insured
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {namedInsured.map((name) => (
                  <Badge key={name} color="zinc" className="text-[10px]">
                    {name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Carrier contact section - unchanged version */}
          {!carrierChanged && (carrierHandles.length > 0 || !!carrier?.address) && (
            <div className="flex-1 space-y-0.5">
              {carrierHandles.map((handle, idx) => {
                const Icon = getHandleIcon(handle.type as string);
                const value =
                  (handle.value as string) ||
                  (handle.address_line1 as string) ||
                  '\u2014';
                return (
                  <div key={idx} className="flex items-center gap-1.5 px-1 py-0.5">
                    <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{value}</span>
                  </div>
                );
              })}
              {!!carrier?.address && (
                <div className="flex items-center gap-1.5 px-1 py-0.5">
                  <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-700 truncate">
                    {String(carrier.address)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Policy Identifiers - unchanged version */}
          {!policyIdentifiersChanged && policyIdentifiers.length > 0 && (
            <CollapsibleSection
              title="Policy Identifiers"
              count={policyIdentifiers.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {policyIdentifiers.map((id, idx) => {
                const idType = id.identifier_type as string;
                const idValue = id.identifier_value as string;
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex-1 flex items-baseline gap-1.5 px-1 py-0.5">
                      <span className="text-xs text-gray-400 shrink-0">
                        {IDENTIFIER_TYPE_LABELS[idType as IdentifierType] ??
                          idType}
                        :
                      </span>
                      <span className="text-sm text-gray-700">{idValue}</span>
                    </div>
                  </div>
                );
              })}
            </CollapsibleSection>
          )}

          {/* Benefits - unchanged version */}
          {!benefitsChanged && benefits.length > 0 && (
            <CollapsibleSection
              title="Benefits"
              count={benefits.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              <div className="flex flex-wrap items-start gap-1.5">
                {benefits.map((benefit, idx) => {
                  const type = benefit.benefit_type as BenefitType | undefined;
                  const limitations = benefit.limitations as Record<
                    string,
                    number
                  > | null;
                  const isStacked = benefit.is_stacked as boolean | null;
                  const hasLimitations =
                    limitations && Object.keys(limitations).length > 0;
                  const limitationsText = hasLimitations
                    ? Object.entries(limitations)
                        .map(
                          ([label, amount]) =>
                            `${formatDollar(amount)}/${label}`,
                        )
                        .join(', ')
                    : null;

                  return (
                    <div key={idx} className="flex items-center gap-1.5">
                      {type && (
                        <Badge color="zinc" className="text-[10px]">
                          {BENEFIT_TYPE_LABELS[type] ?? formatEnumValue(type)}
                        </Badge>
                      )}
                      {limitationsText && (
                        <span className="text-xs text-gray-600">
                          {limitationsText}
                        </span>
                      )}
                      {isStacked != null && (
                        <span className="text-[10px] text-gray-400">
                          {isStacked ? 'Stacked' : 'Not stacked'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>
          )}

          {/* Claims - unchanged version */}
          {!claimsChanged && claims.length > 0 && (
            <CollapsibleSection
              title="Claims"
              count={claims.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {claims.map((claim, idx) => {
                const claimId = claim.claim_identifier as string | undefined;
                const type = claim.benefit_type as BenefitType | undefined;
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-1.5 px-1 py-0.5">
                      <span className="text-sm text-gray-700">
                        {claimId || '\u2014'}
                      </span>
                      {type && (
                        <Badge color="zinc" className="text-[10px]">
                          {BENEFIT_TYPE_LABELS[type] ?? formatEnumValue(type)}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </CollapsibleSection>
          )}

          {/* Adjusters - unchanged version */}
          {!adjustersChanged && adjusters.length > 0 && (
            <CollapsibleSection
              title="Adjusters"
              count={adjusters.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {adjusters.map((adj, idx) => {
                const adjName = adj.name as string | undefined;
                const type = adj.benefit_type as BenefitType | undefined;
                const handles = (adj.handles ?? []) as Array<
                  Record<string, unknown>
                >;
                return (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="flex-1 px-1 py-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm text-gray-700">
                          {adjName || 'Unnamed Adjuster'}
                        </span>
                        {type && (
                          <Badge color="zinc" className="text-[10px]">
                            {BENEFIT_TYPE_LABELS[type] ??
                              formatEnumValue(type)}
                          </Badge>
                        )}
                      </div>
                      {handles.length > 0 && (
                        <div className="mt-0.5 space-y-0.5">
                          {handles.map((h, hIdx) => {
                            const Icon = getHandleIcon(h.type as string);
                            return (
                              <div
                                key={hIdx}
                                className="flex items-center gap-1.5"
                              >
                                <Icon className="h-3 w-3 text-gray-400 shrink-0" />
                                <span className="text-xs text-gray-600 truncate">
                                  {(h.value as string) || '\u2014'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </CollapsibleSection>
          )}

          {/* Vehicles - unchanged version */}
          {!vehiclesChanged && vehicles.length > 0 && (
            <CollapsibleSection
              title="Vehicles"
              count={vehicles.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {vehicles.map((v, idx) => {
                const year = v.year as number | undefined;
                const make = v.make as string | undefined;
                const model = v.model as string | undefined;
                const vin = v.vin as string | undefined;
                const plate = v.license_plate as string | undefined;
                const color = v.color as string | undefined;
                const title = [year, make, model]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="flex-1 px-1 py-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-gray-700">
                          {title || 'Vehicle'}
                        </span>
                        {color && (
                          <span className="text-xs text-gray-400">
                            {color}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                        {vin && <span>VIN: {vin}</span>}
                        {plate && <span>Plate: {plate}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CollapsibleSection>
          )}

          {/* Policy Details - unchanged version */}
          {!policyDetailsChanged && policyDetails.length > 0 && (
            <CollapsibleSection
              title="Policy Details"
              count={policyDetails.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {policyDetails.map((detail, idx) => {
                const key = detail.key as string;
                const value = detail.value as string;
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex-1 flex items-baseline gap-1.5 px-1 py-0.5">
                      <span className="text-xs text-gray-400 shrink-0">
                        {key}:
                      </span>
                      <span className="text-sm text-gray-700 truncate">
                        {value}
                      </span>
                    </div>
                  </div>
                );
              })}
            </CollapsibleSection>
          )}

          {/* Subrogator - unchanged version */}
          {!subrogatorChanged && subrogator && (
            <div className="flex-1">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-xs font-medium text-gray-500">Subrogator</span>
              </div>
              <span className="text-sm text-gray-700">
                {(subrogator.name as string) || 'Unnamed'}
              </span>
            </div>
          )}

          {/* References - unchanged version */}
          {visibleSecondaryFields.some((f) => !changes.isFieldChanged(f.key)) && (
            <CollapsibleSection
              title="References"
              count={visibleSecondaryFields.filter((f) => !changes.isFieldChanged(f.key)).length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {visibleSecondaryFields
                .filter((f) => !changes.isFieldChanged(f.key))
                .map((f) => (
                  <div key={f.key} className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <EntityField
                        label={f.label}
                        value={data[f.key]}
                        fieldKey={f.key}
                        changes={changes}
                      />
                    </div>
                  </div>
                ))}
            </CollapsibleSection>
          )}
        </ShowMoreSection>
      )}

      {/* Transfer button */}
      {checklist.showButton && (
        <TransferButton
          enabled={checklist.buttonEnabled}
          onClick={transfer.handleTransfer}
          onSkip={transfer.handleSkip}
          isLoading={transfer.isLoading}
          status={transfer.status}
        />
      )}
    </EntityChangeCardShell>
  );
};
